import { firestore } from "firebase/app";

/**
 * The dead-battery clock fault, as stored data rather than a render-time repair.
 *
 * **Read side.** IRES Cloud computes and writes these values where the samples are already decoded;
 * Office reads them instead of rebuilding the end date on every render. The fault, the house rule and
 * the storage design are documented in [`docs/devices/panel-clock-corrections.md`](../../../docs/devices/panel-clock-corrections.md).
 *
 * **The rule these types implement (Taimoor, 2026-09-21): detect broadly, correct narrowly.**
 * "I don't want to silently correct something that we are not aware of."
 *
 * - An end date that is one of the three *enumerated* placeholders is correctable: rebuilt silently
 *   for customers, with a `*` for IRES users, exactly as `computeNewEndDate` does today.
 * - An end date that merely precedes its start date is an **anomaly**: recorded and surfaced to IRES,
 *   never rebuilt. 353 chains are in that state, and their values cannot be enumerated because the
 *   clock keeps ticking after the reset (one device carries 24 distinct garbage end dates).
 * - A run whose rebuild would be *knowably wrong* is an anomaly too: on a flattened sample time axis
 *   the reconstruction comes out too early, so the run stays raw (Taimoor, 2026-09-21).
 *
 * **This file is one half of a cross-repo contract.** Its counterpart is IRES Cloud
 * `functions/src/data/models/RunEndDateCorrection.ts`, which owns the writing. The placeholder set,
 * the timezone and the version constants must match. `tests/unit/runEndDateCorrection.spec.ts` pins
 * them here; the Cloud spec pins the same values there.
 */

/**
 * The timezone the placeholder test is evaluated in, in BOTH repos.
 *
 * The existing `hasPlaceholderClockDate` in [`PanelClockPlaceholders.ts`](../../utils/PanelClockPlaceholders.ts)
 * renders the date in the **browser's** zone while Cloud runs in UTC, so the same timestamp can be a
 * different calendar day in the two places. Measured 2026-09-21: of the 4,575 chains that end before
 * they start, **56 land on a different calendar day in UTC than in Europe/Berlin**. None currently
 * straddles a placeholder date, so the two agree today by luck rather than by construction.
 *
 * Aligning the render-time predicate onto this constant is a Phase 3 change, not done yet.
 */
export const CLOCK_PLACEHOLDER_ZONE = "Europe/Berlin";

/**
 * Which algorithm produced a stored correction. Must match the Cloud constant.
 *
 * A reader may refuse a value from a version it does not trust, and a later fix can re-run exactly
 * the rows it affects.
 */
export const END_DATE_CORRECTION_METHOD_VERSION = 1;

/** Which rule decided a stored end date was wrong. Must match the Cloud constant. */
export const END_DATE_DETECTION_VERSION = 1;

/**
 * What is wrong with a run's dates, for the faults we record but never repair.
 *
 * Every member means the same thing to a reader: **show the raw value.**
 */
export enum EndDateAnomalyKind {
  /**
   * The run ends before it starts and we cannot explain the value: the drifted clock tail, 353
   * chains whose end date is neither an enumerated placeholder nor a plausible date.
   */
  END_BEFORE_START = "END_BEFORE_START",
  /**
   * The end date is a placeholder we *could* rebuild, but the run's sample time axis is flattened so
   * the rebuild is **known to come out too early**.
   *
   * **Decided 2026-09-21 (Taimoor): stay raw.** 723 runs are in this state.
   */
  FLATTENED_TIME_AXIS = "FLATTENED_TIME_AXIS",
  /**
   * The end date is a placeholder and so is the **start** date, so there is nothing to anchor a
   * rebuild to: it accumulates elapsed minutes onto the start, and a placeholder start just yields
   * another placeholder-era date. 43% of placeholder-ended runs are in this state.
   */
  PLACEHOLDER_START_DATE = "PLACEHOLDER_START_DATE",
  /**
   * The samples carry no usable time information, so the rebuild produces nothing or lands on the
   * start date itself. 15% of otherwise rebuildable runs are in this state: a zero-length drying is
   * an absence of evidence, not a finding, so the run stays raw (decided 2026-09-21).
   */
  NO_USABLE_TIME_AXIS = "NO_USABLE_TIME_AXIS",
}

/**
 * A rebuilt end date, stored **beside** the raw one and never over it.
 *
 * The raw `endDate` and the run's samples stay exactly as the device sent them. Never write a
 * corrected value over stored device data: once the raw is gone, estimated and measured become
 * indistinguishable and the IRES `*` stops meaning anything.
 *
 * **Its presence means IRES stands behind the value**, which is why it carries no quality grading. A
 * rebuild we would not stand behind is never written: the run gets a {@link RunEndDateAnomaly}
 * instead and `getEffectiveEndDate` therefore returns the raw date. A reader cannot accidentally show
 * a value we know is wrong, because no such value exists to read.
 */
export interface RunEndDateCorrection {
  /** The rebuilt timestamp. Prefer it over the raw `endDate` when present. */
  correctedEndDate: firestore.Timestamp;
  correctedAt: firestore.Timestamp;
  /** `SYSTEM` for the automatic correction, or a user id if one is ever applied by hand. */
  correctedBy: string;
  /** @see END_DATE_CORRECTION_METHOD_VERSION */
  methodVersion: number;
  /** @see END_DATE_DETECTION_VERSION */
  detectionVersion: number;
  /** What was stored, so a reader need not re-derive the fault to explain the value. */
  rawEndDate: firestore.Timestamp;
}

/**
 * A run whose dates are impossible and which we are **not** correcting.
 *
 * Present without a {@link RunEndDateCorrection} is the intended state for the drifted clock values:
 * we know the run is wrong and we do not pretend to fix it. Surface it to IRES users; the customer
 * keeps the real, visibly broken value.
 */
export interface RunEndDateAnomaly {
  kind: EndDateAnomalyKind;
  detectedAt: firestore.Timestamp;
  /** @see END_DATE_DETECTION_VERSION */
  detectionVersion: number;
  /** Whether a correction was also stored. False is the expected case for the drifted tail. */
  corrected: boolean;
}

/**
 * The end date to display for a run: the stored correction when there is one, else the raw value.
 *
 * **The one place this decision is made.** Office and the Cloud PDF renderer reading the same rule
 * through one helper is what stops them drifting apart, which is the whole reason the correction is
 * stored rather than recomputed per consumer.
 *
 * Returns the raw value when no correction is stored, so a run that has not been processed yet, or
 * one whose dates are anomalous but not correctable, keeps showing what the device actually sent.
 */
export function getEffectiveEndDate(run: {
  endDate: firestore.Timestamp;
  endDateCorrection?: RunEndDateCorrection | null;
}): firestore.Timestamp {
  return run.endDateCorrection?.correctedEndDate ?? run.endDate;
}

/**
 * Whether a run's displayed end date came from a correction rather than from the device.
 *
 * Drives the IRES-only `*`. Gate the marker itself on `markEndDateManualCorrectionsAccess`: the
 * customer must not see it.
 */
export function isEndDateCorrected(run: {
  endDateCorrection?: RunEndDateCorrection | null;
}): boolean {
  return run.endDateCorrection != null;
}

/**
 * What a run's stored clock verdict says to do, before any fallback is considered.
 *
 * Kept in this leaf module rather than in `ProgramChain` so the rule can be unit-tested without
 * dragging the whole app in through that file's import chain, and so every consumer decides the same
 * way.
 */
export type StoredEndDateDecision =
  /** A rebuilt date IRES stands behind. Use it; load no samples. */
  | { source: "CORRECTION"; endDate: firestore.Timestamp }
  /** Known wrong and deliberately not rebuilt. Show the raw date; load no samples. */
  | { source: "RAW_KNOWN_WRONG"; kind: EndDateAnomalyKind }
  /** No verdict stored yet, so the caller falls back to its own rebuild. */
  | { source: "NOT_YET_EVALUATED" };

/**
 * Read a run's stored verdict.
 *
 * The three outcomes are deliberately distinct. "Known wrong" is not the same as "not looked at": the
 * first must show the raw value, the second may still be rebuilt at render time until the backfill
 * has reached it.
 */
export function decideStoredEndDate(run: {
  endDateCorrection?: RunEndDateCorrection | null;
  endDateAnomaly?: RunEndDateAnomaly | null;
}): StoredEndDateDecision {
  if (run.endDateCorrection) {
    return { source: "CORRECTION", endDate: run.endDateCorrection.correctedEndDate };
  }

  if (run.endDateAnomaly) {
    return { source: "RAW_KNOWN_WRONG", kind: run.endDateAnomaly.kind };
  }

  return { source: "NOT_YET_EVALUATED" };
}
